import { NameWorkQueueDialogContainer } from '@operations/features/workInstructions/components/organisms/NameWorkQueueDialog'
import { StartWorkQueueDialog } from '@operations/features/workQueues/components/molecules/StartWorkQueueDialog'
import { StopWorkQueueDialog } from '@operations/features/workQueues/components/molecules/StopWorkQueueDialog'
import { useTranslate } from '@tolgee/react'
import { ConfirmationDialog } from '@tom-ui/ui'
import { BlockLeavingPage, PATHS } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { useNavigate } from 'react-router'
import { ProcessingTimeFormProfile } from '../components/ProcessingTime/processing-time-form.profile'
import { ProcessingTime } from '../components/ProcessingTime/ProcessingTime'
import { SplitFormProfile } from '../components/SplitForm/split-form.profile'
import { SplitForm } from '../components/SplitForm/SplitForm'
import { WorkQueueDetailsDialog } from '../components/WorkQueueDetailsDialog/WorkQueueDetailsDialog'
import { WorkQueueFormProfile } from '../components/WorkQueueForm/workqueue-form.profile'
import { WorkQueueForm } from '../components/WorkQueueForm/WorkQueueForm'
import { WorkQueue } from '../models/work-queue.model'
import { CraneSplitContainerUIStore } from '../stores/crane-split-container.ui-store'
import { CraneSplitDialogUIStore } from '../stores/crane-split-dialog.ui-store'

interface Props {
  uiStore: CraneSplitDialogUIStore
  craneSplitContainerUIStore: CraneSplitContainerUIStore
}

export const CraneSplitDialogContainer = observer(
  ({ uiStore, craneSplitContainerUIStore }: Props) => {
    const { t } = useTranslate()
    const navigate = useNavigate()
    const vesselName = uiStore.vesselVisit?.carrierName
    const visitId = uiStore.vesselVisit?.id

    const handleLeavePage = async () => {
      uiStore.reset()
    }

    const handleReset = async () => {
      uiStore.reset()
      uiStore.toggleResetDialog()

      const url = `${PATHS.visits.vesselVisits.url}/${visitId}/dashboard`

      navigate(url)
    }

    const handleRemove = async () => {
      await uiStore.removeWorkQueue()
      uiStore.toggleRemoveDialog()
    }

    const handleEdit = (workQueue: WorkQueue) => {
      handleCloseDetails()
      uiStore.toggleEditWorkQueueDrawer(workQueue)
    }

    const handleSplit = (workQueue: WorkQueue) => {
      handleCloseDetails()
      uiStore.toggleSplit(workQueue)
    }

    const handleOpenRemove = (id: number) => {
      handleCloseDetails()
      uiStore.toggleRemoveDialog(id)
    }

    const handleOpenRename = () => {
      uiStore.toggleRenameDialog()
    }

    const handleCloseDetails = () => {
      uiStore.toggleDetailsDialog()
    }

    const handleProcessingTimeSubmit = (processingTime: ProcessingTimeFormProfile) => {
      uiStore.setProcessingTime(processingTime.processingTime)
      uiStore.toggleProcessingTime()
    }

    const handleCalculateMoves = async (twinContainers?: boolean, newContainersAmount?: number) => {
      return craneSplitContainerUIStore.calculateMoves(
        uiStore.workQueue!,
        twinContainers,
        newContainersAmount,
      )
    }

    const handleWorkQueueSubmit = async (workQueue: WorkQueueFormProfile) => {
      await uiStore.editWorkQueue(workQueue)
      uiStore.toggleEditWorkQueueDrawer()
    }

    const handleSplitSubmit = (splitWorkQueue: SplitFormProfile) => {
      uiStore.splitWorkQueue(splitWorkQueue)
      uiStore.toggleSplit()
    }

    return (
      <>
        <ConfirmationDialog
          open={uiStore.removeDialog}
          title={t('removeWorkQueueConfirmationTitle', 'You are about to remove a queue')}
          message={t(
            'removeWorkQueueConfirmationMessage',
            'All containers of this queue will be merged to the previous one in this bay and will use its data: twin containers, start time and crane.',
          )}
          primaryActionText={t('remove', 'Remove')}
          closeLabel={t('close', 'Close')}
          maxWidth='sm'
          onClose={() => uiStore.toggleRemoveDialog()}
          onConfirm={handleRemove}
        />

        <ConfirmationDialog
          open={uiStore.resetDialog}
          title={t('leaveThisPage', 'Leave this page?')}
          message={t(
            'youHaveUnsavedChangesThatWillBeLostIfYouLeaveThisPage',
            'You have unsaved changes that will be lost if you leave this page.',
          )}
          primaryActionText={t('yesLeave', 'Yes, leave')}
          closeLabel={t('stay', 'Stay')}
          onClose={() => uiStore.toggleResetDialog()}
          onConfirm={handleReset}
        />

        <BlockLeavingPage
          unsavedChanges={craneSplitContainerUIStore.canUndo && !uiStore.resetDialog}
          onConfirm={handleLeavePage}
        />

        <WorkQueueDetailsDialog
          open={uiStore.detailsDialog && !uiStore.startDialog && !uiStore.stopDialog}
          workQueue={uiStore.workQueue!}
          vesselVisitStatus={uiStore.vesselVisit?.status}
          workInstructions={uiStore.selectedWorkQueueWorkInstructions}
          vesselName={vesselName}
          getWorkInstructionForRestow={uiStore.getWorkInstructionForRestow}
          onClose={handleCloseDetails}
          onEdit={handleEdit}
          onStop={id => uiStore.toggleStopDialog(id)}
          onStart={id => uiStore.toggleStartDialog(id)}
          onSplit={handleSplit}
          onRename={handleOpenRename}
          onRemove={handleOpenRemove}
        />

        <StartWorkQueueDialog
          open={uiStore.startDialog}
          workQueueId={uiStore.workQueueId}
          workQueueStatus={uiStore.workQueueFromStore?.status}
          onClose={() => uiStore.toggleStartDialog()}
          onConfirm={() => uiStore.toggleStartDialog()}
        />

        <StopWorkQueueDialog
          open={uiStore.stopDialog}
          workQueueId={uiStore.workQueueId}
          onClose={() => uiStore.toggleStopDialog()}
          onConfirm={() => uiStore.toggleStopDialog()}
        />

        <ConfirmationDialog
          open={uiStore.processingTimeIsOpen}
          title={t('editProcessingTimeQuestion', 'Edit processing time?')}
          maxWidth='xs'
          primaryActionText={t('yesEdit', 'Yes, edit')}
          primaryActionDataCy='crane-split-edit-processing-time-confirm-btn'
          closeLabel={t('noCancel', 'No, cancel')}
          formId='processing-time-form'
          onClose={() => uiStore.toggleProcessingTime()}
        >
          <ProcessingTime
            id='processing-time-form'
            processingTime={uiStore.processingTime}
            onSubmit={handleProcessingTimeSubmit}
          />
        </ConfirmationDialog>

        <ConfirmationDialog
          open={uiStore.editWorkQueueIsOpen}
          title={t('editWorkQueueQuestion', 'Edit work queue?')}
          maxWidth='md'
          primaryActionText={t('yesEdit', 'Yes, edit')}
          primaryActionDataCy='crane-split-edit-work-queue-confirm-btn'
          closeLabel={t('noCancel', 'No, cancel')}
          formId='workqueue-form'
          onClose={() => uiStore.toggleEditWorkQueueDrawer()}
        >
          <WorkQueueForm
            id='workqueue-form'
            onSubmit={handleWorkQueueSubmit}
            workQueueDefaultValues={uiStore.workQueueDefaultValues}
            craneOptions={uiStore.equipmentOptions}
            workQueueBoundary={uiStore.workQueue?.boundary}
            operationTypeOptions={uiStore.operationTypeOptions}
            onCalculateMoves={handleCalculateMoves}
            isUnknownBay={uiStore.workQueue?.isUnknownBay}
            isTwentiesOnlyBay={uiStore.workQueue?.isTwentySingleBay}
          />
        </ConfirmationDialog>

        <ConfirmationDialog
          open={uiStore.splitIsOpen}
          title={t('splitWorkQueueQuestion', 'Split work queue?')}
          primaryActionText={t('yesSplit', 'Yes, split')}
          primaryActionDataCy='crane-split-split-work-queue-confirm-btn'
          closeLabel={t('noCancel', 'No, cancel')}
          formId='split-form'
          maxWidth='md'
          onClose={() => uiStore.toggleSplit()}
        >
          <SplitForm
            id='split-form'
            workQueue={uiStore.workQueue!}
            onSubmit={handleSplitSubmit}
            onCalculateMoves={handleCalculateMoves}
          />
        </ConfirmationDialog>

        <NameWorkQueueDialogContainer
          isOpen={uiStore.renameDialog}
          queue={uiStore.workQueue!}
          handleClose={() => uiStore.toggleRenameDialog()}
          handleSubmit={(id, name) => uiStore.renameQueue(id, name)}
        />
      </>
    )
  },
)
